import Modal from "@/common/components/Modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "lucide-react";
import { BaseSyntheticEvent, useEffect, useState } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { maintenanceSpecialtySchema, MaintenanceSpecialtyValidationSchema } from "../../schemas/maintenanceSpecialty.schema";
import SelectController from "@/common/components/SelectController";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { MaintenanceObjectivePartsData } from "@/features/maintenance/objectiveMaintenance/objectiveParts/types/MaintenanceObjectiveParts.type";
import { useMaintenanceObjectiveParts } from "@/features/maintenance/objectiveMaintenance/objectiveParts/hooks/useMaintenanceObjectiveParts";
import { useMaintenanceObjectiveActions } from "@/features/maintenance/objectiveMaintenance/objectiveActions/hooks/useMaintenanceObjectiveActions";
import { MaintenanceObjectiveActionsData } from "@/features/maintenance/objectiveMaintenance/objectiveActions/types/MaintenanceObjectiveActions.type";
import { controlStyles } from "@/common/components/Select/styles";
import { EstablishmentLeadData } from "../../types/establishmentLead.type";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useCreateEstablishmentLeadMaintenanceSpecialty } from "../../hooks/maintenanceModules/useCreateEstablishmentLeadMaintenanceSpecialty";

interface MaintenanceSpecialtyFieldModalProps {
  establishmentLead: EstablishmentLeadData;
  submit?: (e?: BaseSyntheticEvent) => Promise<void>;
  disabled?: boolean;
}

const CreateMaintenanceSpecialtyModal = ({ establishmentLead, disabled }: MaintenanceSpecialtyFieldModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [searchPart, setSearchPart] = useDebounce('');
  const [searchAction, setSearchAction] = useDebounce('');

  const [systemPartsOptions, setSystemPartsOptions] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<MaintenanceSpecialtyValidationSchema>({
    resolver: zodResolver(maintenanceSpecialtySchema),
    defaultValues: {
      part: undefined,
      system_part: undefined,
      action: undefined,
    },
  });

  const {
    part,
  } = useWatch({
    control,
  });

  const {
    mutate: createMaintenanceSpecialty,
    isPending: createMaintenanceSpecialtyIsPending,
  } = useCreateEstablishmentLeadMaintenanceSpecialty({
    id: establishmentLead.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] });

      setIsOpen(false);
    },
    onError: FormTransformers.errorsTransformer<MaintenanceSpecialtyValidationSchema>({
      setError
    }),
  });


  const onSubmit: SubmitHandler<MaintenanceSpecialtyValidationSchema> = (data) => {
    createMaintenanceSpecialty(data);
  }

  const {
    data: partsOptions,
  } = useMaintenanceObjectiveParts({
    filters: {
      search: searchPart,
    },
    mapper: Mapper.mapToOptionsWithItem<MaintenanceObjectivePartsData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  const {
    data: actionsOptions,
  } = useMaintenanceObjectiveActions({
    filters: {
      search: searchAction,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectiveActionsData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  useEffect(() => {
    setTimeout(reset, 500);
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerDisabled={disabled}
        Trigger={(
          <button
            type="button"
            className="flex flex-col items-center bg-white-100 w-full gap-2 p-2.5 rounded-lg border-2 border-dashed border-white-200 text-blue-800 font-medium"
          >
            <PlusIcon size={16} />

            Adicionar Objeto
          </button>
        )}
        title="Adicionar Objeto de Manutenção"
        useCancellableFooter
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        confirmButtonIsLoading={createMaintenanceSpecialtyIsPending}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 text-white-800">
            <SelectController
              label="Parte"
              control={control}
              name="part"
              options={partsOptions}
              onInputChange={setSearchPart}
              errorMessage={errors.part?.message || undefined}
              markAsRequired
              triggerOnChange={(value) => {
                setSystemPartsOptions((value.item as MaintenanceObjectivePartsData).system_parts.map((system_part) => ({
                  value: system_part.id,
                  label: system_part.system.display_name,
                })));
              }}
              operationKlassToCheckPermission="Admin::Maintenance::ObjectiveParts::Filter"
            />

            <SelectController
              label="Parte do sistema"
              control={control}
              name="system_part"
              options={systemPartsOptions}
              errorMessage={errors.system_part?.message || undefined}
              markAsRequired
              isDisabled={!part}
              placeholder={!part ? 'Selecione a Parte' : undefined}
              classNames={{
                ...controlStyles,
                placeholder: !part ? () => 'italic' : controlStyles.placeholder,
              }}
              operationKlassToCheckPermission="Admin::Maintenance::ObjectiveParts::Filter"
            />

            <SelectController
              label="Ação"
              control={control}
              name="action"
              options={actionsOptions}
              onInputChange={setSearchAction}
              errorMessage={errors.action?.message || undefined}
              markAsRequired
              operationKlassToCheckPermission="Admin::Maintenance::ObjectiveActions::Filter"
            />
          </form>
        )}
      />
    </>
  );
}

export default CreateMaintenanceSpecialtyModal;
